import { Injectable, inject, signal } from '@angular/core';
import { UserStorageService } from './user-storage.service';

const STORAGE_KEY = 'map-display-settings';

export interface MapDisplaySettings {
  /** Hex color for a coverage cell (the low-zoom "blob" layer -- see generate-tiles.ts and
   *  map.component.ts's addRegistryLayers) that contains at least one locally-active address
   *  within the current activity filter. */
  activeRegionColor: string;
  /** fill-opacity for an active coverage cell -- kept separate from inactiveRegionOpacity so
   *  an active cell can read clearly (more opaque) while inactive ones stay a faint outline. */
  activeRegionOpacity: number;
  /** fill-opacity for a coverage cell with no activity in range -- deliberately faint so it
   *  reads as "there's data here" without competing with the basemap. */
  inactiveRegionOpacity: number;
}

// A vivid magenta -- reads clearly against a hybrid/satellite basemap's natural greens,
// browns, and blues, and (deliberately) doesn't match any OUTCOME_STYLES chip color
// (success green, warning orange, danger red, accent blue -- see outcome-style.model.ts), so
// an active coverage blob is never mistaken for signaling a specific outcome the way an
// individual building's tint does.
export const DEFAULT_MAP_DISPLAY_SETTINGS: MapDisplaySettings = {
  activeRegionColor: '#ec4899',
  activeRegionOpacity: 0.45,
  inactiveRegionOpacity: 0.15,
};

/**
 * User-scoped (per-browser) map coverage-layer display preferences -- edited on the general
 * settings page, read by map.component.ts's addRegistryLayers/reloadOwnActivity. Not synced
 * to the backend; a personal display preference like mapStyleId/activityLayerVisible
 * (map.component.ts's own MAP_VIEW_KEY), not app/org data.
 */
@Injectable({ providedIn: 'root' })
export class MapDisplaySettingsService {
  private readonly userStorage = inject(UserStorageService);

  private readonly initial = this.load();
  readonly activeRegionColor = signal(this.initial.activeRegionColor);
  readonly activeRegionOpacity = signal(this.initial.activeRegionOpacity);
  readonly inactiveRegionOpacity = signal(this.initial.inactiveRegionOpacity);

  setActiveRegionColor(value: string): void {
    this.activeRegionColor.set(value);
    this.persist();
  }

  setActiveRegionOpacity(value: number): void {
    this.activeRegionOpacity.set(value);
    this.persist();
  }

  setInactiveRegionOpacity(value: number): void {
    this.inactiveRegionOpacity.set(value);
    this.persist();
  }

  private persist(): void {
    this.userStorage.setTypedItem<MapDisplaySettings>(STORAGE_KEY, {
      activeRegionColor: this.activeRegionColor(),
      activeRegionOpacity: this.activeRegionOpacity(),
      inactiveRegionOpacity: this.inactiveRegionOpacity(),
    });
  }

  private load(): MapDisplaySettings {
    const stored = this.userStorage.getTypedItem<MapDisplaySettings>(STORAGE_KEY);
    return { ...DEFAULT_MAP_DISPLAY_SETTINGS, ...stored };
  }
}
